import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  LayoutDashboard, CheckSquare, Calendar, Megaphone, Heart, ShoppingBag,
  FolderKanban, MapPin, UsersRound, MapPin as MapPinIcon, Clock as ClockIcon,
  LayoutTemplate, Plug, Mail, Key, BookOpen, Rocket, HelpCircle, Target,
  Globe, Shield, Keyboard,
} from 'lucide-react';
import {
  CommandDialog, CommandInput, CommandList, CommandGroup, CommandItem,
} from '../ui/command';
import { useData } from '../../data/DataContext';
import { buildHelpIndex, matchesHelpQuery } from './helpIndex';

const PALETTE_EVENT = 'help:open-palette';

interface JumpTarget {
  label: string;
  to: string;
  icon: React.ElementType;
  keywords?: string;
}

const JUMP_TARGETS: JumpTarget[] = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard, keywords: 'home do this next' },
  { label: 'Deliverables', to: '/deliverables', icon: CheckSquare, keywords: 'tasks kanban list gantt' },
  { label: 'Calendar', to: '/calendar', icon: Calendar, keywords: 'events schedule' },
  { label: 'Content', to: '/content', icon: Megaphone, keywords: 'social media posts' },
  { label: 'Volunteers', to: '/volunteers', icon: Heart, keywords: 'signups opportunities' },
  { label: 'Merch', to: '/merch', icon: ShoppingBag, keywords: 'shop products' },
  { label: 'Projects', to: '/projects', icon: FolderKanban },
  { label: 'Venues', to: '/venues', icon: MapPin, keywords: 'rooms conflicts' },
  { label: 'Team', to: '/team', icon: UsersRound, keywords: 'members roster' },
  { label: 'Presence', to: '/team/presence', icon: MapPinIcon, keywords: 'on site check-in' },
  { label: 'Hours', to: '/team/hours', icon: ClockIcon, keywords: 'timesheet' },
  { label: 'Templates', to: '/templates', icon: LayoutTemplate, keywords: 'blueprint project' },
  { label: 'Integrations', to: '/integrations', icon: Plug, keywords: 'canva slack google' },
  { label: 'Daily Digest', to: '/digest', icon: Mail },
  { label: 'API Settings', to: '/settings/api', icon: Key, keywords: 'openai key' },
  { label: 'Focus Mode', to: '/focus', icon: Target, keywords: 'pomodoro' },
  { label: 'TV Dashboard', to: '/tv', icon: LayoutDashboard, keywords: 'wall display' },
  { label: 'Check-in Kiosk', to: '/checkin', icon: Shield },
  { label: 'Public Board', to: '/board', icon: Globe },
  { label: 'Admin Inbox', to: '/inbox', icon: Mail },
  { label: 'Help & Wiki', to: '/help', icon: BookOpen },
];

export function HelpSearchDialog() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { helpArticles } = useData();
  const index = useMemo(() => buildHelpIndex(helpArticles), [helpArticles]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K'))) return;
      // ⌘K works everywhere, including inside inputs and textareas — matches
      // Linear/Notion/Slack convention. Address bar ⌘K isn't dispatched to the page.
      e.preventDefault();
      setOpen(prev => !prev);
    };
    const onCustom = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(PALETTE_EVENT, onCustom);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(PALETTE_EVENT, onCustom);
    };
  }, []);

  // Start every open with a clean query so stale results don't flash.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  function goto(to: string) {
    setOpen(false);
    navigate(to);
  }

  // We filter ourselves (cmdk's shouldFilter is off) because our haystacks
  // hold entire article bodies, which its fuzzy scorer chokes on.
  const articles = index.filter(e => e.type === 'article' && matchesHelpQuery(e.haystack, query));
  const guides = index.filter(e => e.type === 'setup-guide' && matchesHelpQuery(e.haystack, query));
  const glossary = index.filter(e => e.type === 'glossary' && matchesHelpQuery(e.haystack, query));
  const jumpTargets = JUMP_TARGETS.filter(t =>
    matchesHelpQuery(`jump ${t.label} ${t.keywords || ''} ${t.to}`.toLowerCase(), query),
  );
  const showShortcuts = matchesHelpQuery('shortcuts help keyboard question mark', query);
  const hasResults =
    articles.length || guides.length || glossary.length || jumpTargets.length || showShortcuts;

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false} title="Command palette" description="Search articles and jump to pages">
      <CommandInput placeholder="Search help or jump to a page…" value={query} onValueChange={setQuery} />
      <CommandList>
        {!hasResults ? (
          <div className="py-6 text-center text-sm text-slate-500">No matches. Try a different word.</div>
        ) : null}

        {articles.length ? (
          <CommandGroup heading="Articles">
            {articles.map(a => (
              <CommandItem
                key={a.id}
                value={a.id}
                onSelect={() => goto(`/help/${a.slug}`)}
              >
                <HelpCircle className="mr-2 h-4 w-4 text-slate-500" />
                <div className="min-w-0 flex-1">
                  <div className="truncate">{a.title}</div>
                  <div className="truncate text-xs text-slate-500">{a.preview}</div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {guides.length ? (
          <CommandGroup heading="Setup guides">
            {guides.map(a => (
              <CommandItem
                key={a.id}
                value={a.id}
                onSelect={() => goto(`/help/${a.slug}`)}
              >
                <Rocket className="mr-2 h-4 w-4 text-amber-600" />
                <div className="min-w-0 flex-1">
                  <div className="truncate">{a.title}</div>
                  <div className="truncate text-xs text-slate-500">{a.preview}</div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {glossary.length ? (
          <CommandGroup heading="Glossary">
            {glossary.map(a => (
              <CommandItem
                key={a.id}
                value={a.id}
                onSelect={() => goto(`/help/${a.slug}`)}
              >
                <BookOpen className="mr-2 h-4 w-4 text-indigo-600" />
                <div className="min-w-0 flex-1">
                  <div className="truncate">{a.title}</div>
                  <div className="truncate text-xs text-slate-500">{a.preview}</div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {jumpTargets.length ? (
          <CommandGroup heading="Jump to page">
            {jumpTargets.map(target => {
              const Icon = target.icon;
              return (
                <CommandItem
                  key={target.to}
                  value={target.to}
                  onSelect={() => goto(target.to)}
                >
                  <Icon className="mr-2 h-4 w-4 text-slate-500" />
                  <span>{target.label}</span>
                  <span className="ml-auto text-xs text-slate-400">{target.to}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ) : null}

        {showShortcuts ? (
          <CommandGroup heading="Shortcuts">
            <CommandItem
              value="shortcuts"
              onSelect={() => {
                setOpen(false);
                window.dispatchEvent(new KeyboardEvent('keydown', { key: '?' }));
              }}
            >
              <Keyboard className="mr-2 h-4 w-4 text-slate-500" />
              Show keyboard shortcuts overlay
            </CommandItem>
          </CommandGroup>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}
